import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Shield,
  UserCircle,
  ClipboardList,
  Phone,
  CreditCard,
  Leaf,
  ArrowRight,
  FileText,
  Stethoscope,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  Shield: Shield,
  UserCircle: UserCircle,
  ClipboardList: ClipboardList,
  Phone: Phone,
  CreditCard: CreditCard,
  Leaf: Leaf,
};

const quickLinks = [
  { icon: FileText, label: "Medical Records Request", href: "#" },
  { icon: CreditCard, label: "Pay Your Bill Online", href: "#" },
  { icon: Stethoscope, label: "Find a Doctor", href: "/doctors" },
  { icon: HelpCircle, label: "Insurance Verification", href: "#" },
];

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await axios.get(`${API}/resources`);
        setResources(response.data);
      } catch (err) {
        console.error("Error fetching resources:", err);
      }
    };
    fetchResources();
  }, []);

  return (
    <div data-testid="resources-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#ff9e00]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Patient Resources</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              Patient <span className="text-gradient">Resources</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Everything you need to know before, during, and after your visit. We're here to support you every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => (
              <Link 
                key={index}
                to={link.href}
                data-testid={`quick-link-${index}`}
                className="glass rounded-xl p-4 flex items-center gap-3 hover:border-[#00f0ff] transition-all group"
              >
                <link.icon className="w-6 h-6 text-[#00f0ff] shrink-0" />
                <span className="text-white text-sm font-medium group-hover:text-[#00f0ff] transition-colors">
                  {link.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Information Hub</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Helpful Information
            </h2>
            <p className="text-gray-400">Resources to help you navigate your healthcare journey</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource, index) => {
              const IconComponent = iconMap[resource.icon] || Shield;
              const colors = ["#00f0ff", "#ff00ff", "#ff9e00"];
              const color = colors[index % colors.length];
              
              return (
                <div
                  key={resource.id}
                  data-testid={`resource-card-${resource.id}`}
                  className="resource-card"
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    <IconComponent className="w-7 h-7" style={{ color }} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{resource.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{resource.content}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Patient Portal Section */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Online Access</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                Patient Portal
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Access your health information anytime, anywhere through our secure patient portal. Manage appointments, view test results, and communicate with your care team.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  "View test results and medical records",
                  "Schedule and manage appointments",
                  "Request prescription refills",
                  "Message your healthcare providers",
                  "Pay bills and view statements",
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#00f0ff]" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Button 
                  data-testid="portal-login"
                  className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all"
                >
                  Login to Portal
                </Button>
                <Button 
                  data-testid="portal-register"
                  variant="outline"
                  className="border-2 border-[#4b0082] text-white rounded-full px-8 py-6 hover:border-[#ff00ff] hover:text-[#ff00ff] transition-all"
                >
                  Register Now
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="glass rounded-3xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00f0ff] to-[#ff00ff] flex items-center justify-center">
                    <UserCircle className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Welcome Back</h4>
                    <p className="text-gray-400 text-sm">Access your health dashboard</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-[#0f0518] rounded-xl p-4 border border-[#4b0082]">
                    <div className="text-sm text-gray-400 mb-1">Next Appointment</div>
                    <div className="text-white font-medium">December 15, 2025 at 10:00 AM</div>
                    <div className="text-[#00f0ff] text-sm">Dr. Sarah Mitchell - Primary Care</div>
                  </div>
                  
                  <div className="bg-[#0f0518] rounded-xl p-4 border border-[#4b0082]">
                    <div className="text-sm text-gray-400 mb-1">Recent Lab Results</div>
                    <div className="text-white font-medium">Blood Work - Complete</div>
                    <div className="text-[#00f0ff] text-sm">View Results →</div>
                  </div>
                  
                  <div className="bg-[#0f0518] rounded-xl p-4 border border-[#4b0082]">
                    <div className="text-sm text-gray-400 mb-1">Messages</div>
                    <div className="text-white font-medium">2 New Messages</div>
                    <div className="text-[#ff00ff] text-sm">Read Messages →</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Coverage</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-4">
              Insurance We Accept
            </h2>
            <p className="text-gray-400">We work with most major insurance providers to make healthcare accessible</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {[
              "Medicare",
              "Medicaid",
              "Blue Cross Blue Shield",
              "Aetna",
              "UnitedHealthcare",
              "Cigna",
              "Humana",
              "Kaiser Permanente",
              "Tricare",
              "Anthem",
            ].map((insurance, index) => (
              <div
                key={index}
                data-testid={`insurance-${index}`}
                className="px-6 py-3 bg-[#1a0b2e] border border-[#4b0082] rounded-full text-gray-300"
              >
                {insurance}
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-400 mb-4">Don't see your insurance? Contact us to verify coverage.</p>
            <Link to="/contact">
              <Button 
                data-testid="verify-insurance"
                variant="outline"
                className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold rounded-full px-8 py-6 hover:bg-[#ff00ff] hover:text-white transition-all"
              >
                Verify Insurance Coverage
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Have More Questions?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Our team is here to help. Contact us for any questions about your care.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button 
                data-testid="resources-cta-contact"
                className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
              >
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/appointment">
              <Button 
                data-testid="resources-cta-appointment"
                variant="outline"
                className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#ff00ff] hover:text-white transition-all"
              >
                Book Appointment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
